import React, { useState, MouseEvent, useMemo } from 'react';

import styled from 'styled-components';

import { track } from '@adsk/acs-web-analytics';
import { EntitlementsProject } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import ContentLoader from '@adsk/alloy-react-content-loader';
import { TriangleDownIcon, TriangleUpIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';

import { ProductsList } from './ProductsList';
import { AnalyticsEvents, PRODUCT_NAME_MAX_LENGTH } from '../../../constants';
import { Product } from '../../../models';

const QuickAccessLoaderItem = () => (
  <ContentLoader width={100} height={20} viewBox="0 0 100 20">
    <rect x="0" y="0" rx="10" ry="10" width="100" height="20" />
  </ContentLoader>
);

const QuickAccessProductsButton = styled(BasicButton)({
  padding: '0 20px 0 0',
  width: '100%',
  justifyContent: 'start',
  color: theme.colors.charcoal900,
  '&:hover': {
    color: theme.colors.charcoal700,
  },
  [theme.mediaQuery.small]: {
    width: 'auto',
  },
});

type ProductsListContainerProps = {
  disabled?: boolean;
  isLink: boolean;
  project: EntitlementsProject;
  currentProduct: Omit<Product, 'status'>;
  onProductClick?: (
    event: MouseEvent,
    { url, id }: { url: string; id: string },
  ) => void;
};

export const ProductsListContainer = ({
  disabled,
  isLink,
  project,
  currentProduct,
  onProductClick,
}: ProductsListContainerProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const TriangleIconComponent = useMemo(
    () => (menuOpen ? TriangleUpIcon : TriangleDownIcon),
    [menuOpen],
  );

  return (
    <Flyout
      show={menuOpen}
      onHide={() => setMenuOpen(false)}
      onShow={() => setMenuOpen(true)}
      offset={[0, 15]}
      containerPadding={0}
      placement={Flyout.PLACEMENTS.BOTTOM_START}
      content={
        <ProductsList
          isLink={isLink}
          products={project.products as Product[]}
          selectedProductId={currentProduct.key}
          onProductClick={(...args) => {
            onProductClick?.(...args);
            setMenuOpen(!menuOpen);
          }}
        />
      }
    >
      <QuickAccessProductsButton
        disabled={disabled}
        onClick={() => {
          setMenuOpen(!menuOpen);
          if (!menuOpen) {
            track(AnalyticsEvents.productEntryFlowOpen, {
              project_name: project.name,
            });
          }
        }}
      >
        {disabled ? (
          <QuickAccessLoaderItem />
        ) : (
          <>
            <Box
              display="inline-flex"
              borderRadius={4}
              width={32}
              height={32}
              bg="adskBlue100"
              alignItems="center"
              justifyContent="center"
            >
              {currentProduct.iconUrl && (
                <span
                  style={{
                    maskImage: `url(${currentProduct.iconUrl})`,
                    WebkitMaskImage: `url(${currentProduct.iconUrl})`,
                    backgroundColor: theme.colors.adskBlue500,
                    width: 24,
                    height: 24,
                  }}
                />
              )}
            </Box>
            <span
              style={{
                ...(currentProduct.name?.length > PRODUCT_NAME_MAX_LENGTH
                  ? { ...theme.typography.bodySmall, textAlign: 'left' }
                  : theme.typography.bodyMedium),
                ...{
                  maxWidth: 250,
                  paddingLeft: '8px',
                },
              }}
            >
              {currentProduct.name}
            </span>
            <TriangleIconComponent
              style={{
                color: theme.colors.charcoal700,
                margin: '0 4px',
                [theme.mediaQuery.small]: {
                  visibility: 'hidden',
                },
              }}
              size={20}
            />
          </>
        )}
      </QuickAccessProductsButton>
    </Flyout>
  );
};
